/**
 * LibraryHubSlideOver — the chat-first Library Hub shell.
 *
 * Replaces the modal Dialog at addin/src/variant-c/components/
 * LibraryHub.tsx (legacy) with a slide-over from the right edge,
 * leaving an 18px strip of chat visible on the left as the "obvious
 * close" affordance per Spec 34 §"Implementation milestones" and
 * the design handoff README.
 *
 * Owns:
 *   - Backdrop fade-in (180ms) + panel slide-right (220ms cubic-
 *     bezier(0.2, 0.8, 0.2, 1)) animations.
 *   - Internal mode dispatch — library-list / empty / creating /
 *     uploading / list / detail.
 *   - A real back-stack: the header back button mirrors the writer's
 *     drill path (library-list → list → detail).
 *   - Header chrome with sign-off badge + close ×.
 *   - ESC-to-close + initial focus trap on the close button.
 *   - Backdrop click → onClose.
 *
 * Does NOT own:
 *   - Library list fetch (parent passes ``libraries`` + ``library``).
 *   - Per-mode content (each mode is a separate component the
 *     shell renders by name).
 *   - The selected library state — the shell forwards row-body
 *     selection and chevron-based inspection as separate intents.
 */

import { makeStyles } from "@fluentui/react-components";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { fonts, inkColors, surfaceColors } from "../../tokens";
import { useHubNavigation } from "../hooks/useHubNavigation";
import { HubNavShell } from "./HubNavShell";
import { HubTransition } from "./HubTransition";

// Per Spec 34 §"Implementation milestones" + the design handoff
// README §"Interaction notes": backdrop fade 180ms, slide panel
// 220ms cubic-bezier(0.2, 0.8, 0.2, 1). Kept as literal durations
// here rather than tokens because they're animation-specific to
// this component (no other surface uses these values).
const BACKDROP_FADE_MS = 180;
const PANEL_SLIDE_MS = 220;
import type { InventoryResolveEvent, RunAuditLibrary } from "../../../api/client";
import type { Library, Workspace } from "../../../types";
import { AuditLibraryPanel } from "../../components/inspector/AuditLibraryPanel";
import type { DocumentRow } from "../types";
import { HubCreating, type LibraryScope } from "./HubCreating";
import { HubDetail } from "./HubDetail";
import { HubEmpty } from "./HubEmpty";
import { HubLibraryList } from "./HubLibraryList";
import { HubList } from "./HubList";
import { HubUploading } from "./HubUploading";

/**
 * Hub modes. ``library-list`` is the workspace-level index (M8 —
 * shipped alongside the per-library inspect chevron in the
 * QuickPicker); ``empty`` survives for the no-libraries-yet case in
 * the workspace. The remaining modes are the per-library views.
 */
export type HubMode = "library-list" | "empty" | "creating" | "uploading" | "list" | "detail";

export interface LibraryHubSlideOverProps {
  /** Slide-over visibility. False → component returns null
   *  (no orphan DOM, no animation cost). */
  open: boolean;
  /** Close affordances: backdrop click, header ×, ESC. */
  onClose: () => void;
  /** Active library — drives the header in per-library modes.
   *  ``null`` is valid (e.g. in ``library-list``, ``empty``,
   *  ``creating``). */
  library: Library | null;
  /** All libraries in the workspace — rendered in ``library-list``
   *  mode. */
  libraries: Library[];
  /** Initial mode. Once open, the shell may navigate internally
   *  (e.g. ``library-list → list`` via the row click,
   *  ``list → detail`` via a doc click, etc.). */
  mode: HubMode;
  /** Per-doc rows the unified Hub renders — same array drives
   *  ``uploading`` (filter to in-flight) and ``list`` (full set).
   *  The shell does not slice; sub-views handle filtering. */
  rows: DocumentRow[];
  /** Workspace name for the header subtitle when no library is
   *  active (library-list, creating, empty modes). */
  workspaceName: string;
  /** Workspaces available to the writer — powers the library-list
   *  switcher. Omit to hide it. */
  workspaces?: Workspace[];
  /** Active workspace id — the switcher's selected option. */
  selectedWorkspaceId?: string | null;
  /** Fires on a workspace change in the library-list switcher. Parent
   *  persists it and cascades the library re-fetch. */
  onSelectWorkspace?: (workspaceId: string) => void;
  /** "Create & add files" submission — parent handles the API call
   *  (create + select + inventory refresh). The shell awaits it, then
   *  lands the writer on the standard library ``list`` view. May throw
   *  on failure so the shell can keep the writer on the create form. */
  onCreateLibrary: (input: { name: string; scope: LibraryScope }) => void | Promise<void>;
  /** True while a create call is in flight; passed through to
   *  HubCreating's submit-button disabled state. */
  creating?: boolean;
  /** Row-body pick from ``library-list`` — parent handles the
   *  normal selection flow (chat emit, close, orchestration). */
  onSelectLibrary: (libraryId: string) => void;
  /** When set, renders a "Continue without a library" action in the
   *  ``library-list`` view. Used by the build-on-existing-document flow, where
   *  a source library is optional — the audit library is bootstrapped from the
   *  document's own content. Absent for the normal fresh-draft flow (a library
   *  is required to draft from). */
  onSkipLibrary?: () => void;
  /** Chevron pick from ``library-list`` — parent updates the
   *  inspected library while the shell stays open. */
  onInspectLibrary: (libraryId: string) => void;
  /** Drag-drop / "+ Add files" → parent's upload flow. Wired from
   *  HubEmpty (drag-drop only — its CTA is "Create library", not
   *  "Add") and from HubList (both drag-drop AND the explicit
   *  "+ Add files" button). */
  onAddFiles?: (files: File[]) => void;
  /** "Create artefact" from HubList's "+" menu → parent opens the
   *  artefact builder for the current library. */
  onCreateArtefact?: () => void;
  /** Click on a row in HubList → opens HubDetail. The shell flips
   *  ``mode`` to ``detail`` and remembers the selected doc. */
  onPickDoc?: (docId: string) => void;
  /** Delete a document from the library. */
  onDeleteDoc?: (row: DocumentRow) => void;
  /** Download a document's original stored file. */
  onDownloadDoc?: (row: DocumentRow) => void;
  /** Refresh the classification status for the current library. */
  onRefreshLibrary?: () => void;
  /** "✓ Verify & save" in HubDetail — parent handles persistence;
   *  per Spec 34 DR-5, sign-off is the row-level ``verified`` flag
   *  and editing tags drops it (the parent reapplies on save). */
  onVerifyDoc?: (input: {
    doc_id: string;
    tags: Record<string, string | null>;
    source_doctype?: string;
  }) => void;
  /** Available source doctype options for the HubDetail change dropdown.
   *  Derived from the library's target_doctype by the shell. */
  docTypeOptions?: readonly string[];
  /** True while a verify call is in flight. */
  savingVerify?: boolean;
  /** True while the inventory is being fetched/classified. */
  loading?: boolean;
  /** Current progress message from the SSE stream (e.g. "Labelled foo.pdf"). */
  loadingMessage?: string;
  /**
   * Latest raw SSE event from `streamLibraryInventory` — passed
   * through to HubList to enable the streaming intelligence UX.
   * Zero-touch to the SSE contract.
   */
  resolveEvent?: InventoryResolveEvent | null;
  /** Active run id, or null when no run is open. The Source↔Audit toggle only
   *  appears when a run is loaded AND that run has an audit library, so the
   *  workspace-scoped Library Hub never shows a stray run-scoped view. */
  runId?: string | null;
  /** The active run's dynamic audit library (precedent-aware audit). Fetched by
   *  the parent; the toggle/panel self-hide when this is absent or disabled. */
  auditLibrary?: RunAuditLibrary | null;
  /** Re-fetch the audit library (wired to the panel's Refresh button). */
  onRefreshAuditLibrary?: () => void;
}

const useStyles = makeStyles({
  backdrop: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: "rgba(15, 10, 30, 0.18)",
    zIndex: 80,
    animationName: {
      from: { opacity: 0 },
      to: { opacity: 1 },
    },
    animationDuration: `${BACKDROP_FADE_MS}ms`,
    animationTimingFunction: "ease-out",
  },
  panel: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    width: "calc(100% - 18px)",
    height: "100vh",
    backgroundColor: surfaceColors.paper,
    borderLeft: `1px solid ${surfaceColors.line}`,
    boxShadow: "-12px 0 28px -10px rgba(15, 10, 30, 0.18)",
    zIndex: 81,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    animationName: {
      from: { transform: "translateX(8%)", opacity: 0 },
      to: { transform: "translateX(0)", opacity: 1 },
    },
    animationDuration: `${PANEL_SLIDE_MS}ms`,
    animationTimingFunction: "cubic-bezier(0.2, 0.8, 0.2, 1)",
  },

  modePlaceholder: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "32px 24px",
    textAlign: "center",
    fontFamily: fonts.body,
    fontSize: "11px",
    color: inkColors.ink3,
  },
  // Source↔Audit segmented toggle, shown below the header only when the active
  // run has an audit library.
  toggleBar: {
    display: "flex",
    gap: "4px",
    padding: "8px 12px 0",
    flex: "0 0 auto",
  },
  toggleBtn: {
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "6px",
    background: surfaceColors.paper,
    color: inkColors.ink3,
    fontFamily: fonts.body,
    fontSize: "11px",
    fontWeight: 600,
    padding: "5px 12px",
    cursor: "pointer",
  },
  toggleBtnActive: {
    background: "#1565C0",
    color: "#FFFFFF",
    border: "1px solid #1565C0",
  },
  auditPane: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    padding: "10px 12px 24px",
  },
  skipRow: {
    padding: "12px 16px 20px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  skipButton: {
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    textAlign: "left",
    fontSize: "13px",
    fontWeight: 600,
    color: "var(--colorBrandForeground1, #5b4bd6)",
    ":hover": { textDecoration: "underline" },
  },
  skipHint: {
    fontSize: "11px",
    color: "var(--colorNeutralForeground3, #6b6b6b)",
    lineHeight: 1.4,
  },
});

export const LibraryHubSlideOver: React.FC<LibraryHubSlideOverProps> = ({
  open,
  onClose,
  library,
  libraries,
  mode: initialMode,
  rows,
  workspaceName,
  workspaces,
  selectedWorkspaceId,
  onSelectWorkspace,
  onCreateLibrary,
  creating = false,
  onSelectLibrary,
  onSkipLibrary,
  onInspectLibrary,
  onAddFiles,
  onCreateArtefact,
  onPickDoc,
  onVerifyDoc,
  onDeleteDoc,
  onDownloadDoc,
  onRefreshLibrary,
  docTypeOptions,
  savingVerify = false,
  loading = false,
  loadingMessage = "Loading library…",
  resolveEvent = null,
  runId = null,
  auditLibrary = null,
  onRefreshAuditLibrary,
}) => {
  const styles = useStyles();
  const { innerMode, selectedDocId, direction, back, navigateTo, navigateBack, selectDoc } = useHubNavigation(
    initialMode,
    open,
  );

  // ESC closes. Effect-bound listener so we don't leak when the
  // shell un-mounts.
  useEffect(() => {
    if (!open) return;
    const handler = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Initial focus trap — focus the close button on open so screen
  // readers + keyboard users land at a sensible anchor. Per
  // principles §1: small, single-responsibility focus management.
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (open) closeBtnRef.current?.focus();
  }, [open]);

  // Create-form submission state. Owned here so the form stays
  // mounted (with an inline error) if the create call fails, and only
  // advances to the library view on success.
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Source↔Audit view toggle — local state, deliberately NOT a HubMode, so the
  // header back-stack (library-list → list → detail) is unaffected. Reset to the
  // source view each time the slide-over opens.
  const [view, setView] = useState<"source" | "audit">("source");
  useEffect(() => {
    if (open) setView("source");
  }, [open]);

  const handleCreateFromEmpty = useCallback(() => {
    setCreateError(null);
    navigateTo("creating");
  }, [navigateTo]);

  const handleCreateSubmit = useCallback(
    async (input: { name: string; scope: LibraryScope }) => {
      // Parent owns the create-library API call (create + select +
      // inventory refresh). Await it so ``selectedLibraryId`` is set
      // before we navigate — then land on the standard ``list`` view,
      // the SAME page shown when opening an existing library, where
      // documents are listed and uploaded (in-flight uploads show
      // progress inline as merged rows). On failure, stay on the form
      // and surface the error instead of stranding the writer.
      setCreateSubmitting(true);
      setCreateError(null);
      try {
        await onCreateLibrary(input);
        navigateTo("list");
      } catch (err) {
        setCreateError(err instanceof Error ? err.message : "Couldn't create the library. Please try again.");
      } finally {
        setCreateSubmitting(false);
      }
    },
    [onCreateLibrary, navigateTo],
  );

  const handleCreateCancel = useCallback(() => {
    setCreateError(null);
    navigateTo("library-list");
  }, [navigateTo]);

  if (!open) return null;

  // The Source↔Audit toggle appears only when a run is open AND that run has an
  // audit library present (the feature is enabled and a ref exists). Otherwise
  // the Hub behaves exactly as before, showing only the source views.
  const auditAvailable = Boolean(runId && auditLibrary?.enabled && auditLibrary?.audit_library);
  const effectiveView: "source" | "audit" = auditAvailable ? view : "source";

  // Stable key for HubTransition — "detail-<id>" so switching docs
  // triggers the slide animation even when the mode stays "detail".
  const transitionKey = innerMode === "detail" && selectedDocId ? `detail-${selectedDocId}` : innerMode;

  // Filename for the detail breadcrumb.
  const selectedDocFilename =
    innerMode === "detail" && selectedDocId ? (rows.find((r) => r.doc_id === selectedDocId)?.filename ?? null) : null;

  return (
    <>
      <div
        className={styles.backdrop}
        onClick={onClose}
        onKeyDown={() => {}}
        aria-hidden="true"
        data-testid="hub-backdrop"
      />
      <dialog open className={styles.panel} aria-label="Library Hub" data-testid="hub-panel">
        <HubNavShell
          mode={innerMode}
          workspaceName={workspaceName}
          library={library}
          docFilename={selectedDocFilename}
          back={back}
          closeBtnRef={closeBtnRef}
          onBack={navigateBack}
          onClose={onClose}
        />

        {auditAvailable ? (
          <div className={styles.toggleBar} role="tablist" aria-label="Library view">
            <button
              type="button"
              role="tab"
              aria-selected={effectiveView === "source"}
              className={`${styles.toggleBtn} ${effectiveView === "source" ? styles.toggleBtnActive : ""}`}
              onClick={() => setView("source")}
            >
              Source library
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={effectiveView === "audit"}
              className={`${styles.toggleBtn} ${effectiveView === "audit" ? styles.toggleBtnActive : ""}`}
              onClick={() => setView("audit")}
            >
              Audit library
            </button>
          </div>
        ) : null}

        {effectiveView === "audit" ? (
          <div className={styles.auditPane} data-testid="hub-audit-pane">
            <AuditLibraryPanel data={auditLibrary} onRefresh={onRefreshAuditLibrary} />
          </div>
        ) : (
        <HubTransition modeKey={transitionKey} direction={direction}>
          {innerMode === "library-list" && (
            <HubLibraryList
              libraries={libraries}
              activeLibraryId={library?.id ?? null}
              onPickLibrary={onSelectLibrary}
              onInspectLibrary={(id) => {
                onInspectLibrary(id);
                navigateTo("list");
              }}
              onCreateLibrary={() => navigateTo("creating")}
              workspaces={workspaces}
              selectedWorkspaceId={selectedWorkspaceId}
              onSelectWorkspace={onSelectWorkspace}
            />
          )}
          {innerMode === "library-list" && onSkipLibrary && (
            <div className={styles.skipRow}>
              <button
                type="button"
                className={styles.skipButton}
                onClick={onSkipLibrary}
                data-testid="hub-skip-library"
              >
                Continue without a library →
              </button>
              <span className={styles.skipHint}>
                Building on this document — a source library is optional. You can connect one later.
              </span>
            </div>
          )}
          {innerMode === "empty" && <HubEmpty onCreate={handleCreateFromEmpty} />}
          {innerMode === "creating" && (
            <HubCreating
              onCreate={handleCreateSubmit}
              onCancel={handleCreateCancel}
              submitting={createSubmitting || creating}
              error={createError}
            />
          )}
          {innerMode === "uploading" && <HubUploading rows={rows} />}
          {innerMode === "list" && (
            <HubList
              rows={rows}
              loading={loading}
              loadingMessage={loadingMessage}
              resolveEvent={resolveEvent}
              onPickDoc={(row) => {
                if (row.doc_id) {
                  selectDoc(row.doc_id);
                  onPickDoc?.(row.doc_id);
                }
              }}
              onAddFiles={onAddFiles ?? (() => {})}
              onCreateArtefact={onCreateArtefact}
              onDeleteDoc={onDeleteDoc}
              onDownloadDoc={onDownloadDoc}
              onRefresh={onRefreshLibrary}
            />
          )}
          {innerMode === "detail" &&
            (() => {
              const selected = rows.find((r) => r.doc_id === selectedDocId);
              if (!selected) {
                // Race or stale selection — bounce back to list.
                return (
                  <div className={styles.modePlaceholder} data-testid="hub-detail-placeholder">
                    Document no longer in this library.
                  </div>
                );
              }
              return (
                <HubDetail
                  row={selected}
                  onBack={navigateBack}
                  onVerify={onVerifyDoc ?? (() => {})}
                  docTypeOptions={docTypeOptions}
                  saving={savingVerify}
                />
              );
            })()}
        </HubTransition>
        )}
      </dialog>
    </>
  );
};

export default LibraryHubSlideOver;
